import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { JsonHierarchyElement } from '@psnb/nwt-api';
import {
  BaseResponseDTO,
  CurrentTypeDTO,
  FuelTankEntity,
  FuelTankSizeDTO,
  GeneratorSearchDTO,
  MaintenanceTypeDTO,
  ManufactureTypeDTO,
  ModelTypeDTO,
  StatesEntity,
  StatusTypeDTO,
  TrailerEntity,
  TrailerNameIdDTO,
  VoltageTypeDTO
} from '@psnb/psnb-api';
import {
  AuthService,
  GeneratorService,
  GetHierarchyService,
  isDefined,
  isDefinedNotNull,
  isDefinedNotNullNotEmpty,
  isEmpty,
  isUndefined,
  isUndefinedOrNull,
  isUndefinedOrNullOrEmpty,
  PowerSupplyService,
  UserPermission
} from '@psnb/services';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, take, takeUntil } from 'rxjs/operators';
import { CommonService } from '../../common/common.service';
import { PowerSupplyMessageDialog } from '../../common/message-dialog/power-supply-message-dialog';
import { MessageService } from '../message.service';
import { GpsValidation } from '../power-supply-generator-create/gps.validator';
import { PowerSupplyGeneratorEditViewModel } from './power-supply-generator-edit.model';

/**
 * Mobile component for editing power supply generator, trailer, and fuel tank assets.
 * Follows the same structure and patterns as PowerSupplyGeneratorCreateComponent.
 *
 * Features:
 * - QR code scanner with zoom functionality for asset ID fields
 * - GPS coordinate validation for location tracking
 * - Reactive forms for GPS validation
 * - Template-driven forms for asset data
 * - Change detection optimization with OnPush strategy
 * - Memory leak prevention with takeUntil pattern
 * - Loading state management with progress bar
 * - Multi-tab interface for information organization
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'nwt-power-supply-generator-edit',
  styleUrls: ['./power-supply-generator-edit.component.scss'],
  templateUrl: './power-supply-generator-edit.component.html'
})
export class PowerSupplyGeneratorEditComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * View model containing all component state and data
   */
  public vm: PowerSupplyGeneratorEditViewModel;

  // Public ViewChild references for forms (only one will be defined at a time based on assetType)
  @ViewChild('fuelTankEditForm') public fuelTankEditForm?: NgForm;
  @ViewChild('generatorEditForm') public generatorEditForm?: NgForm;
  @ViewChild('trailerEditForm') public trailerEditForm?: NgForm;

  // Private ViewChild references for scanner elements
  @ViewChild('focusFrame', { read: ElementRef }) private focusFrameElement?: ElementRef<HTMLElement>;
  @ViewChild('fuelTankScanner', { read: ElementRef }) private fuelTankScannerElement?: ElementRef<HTMLElement>;
  @ViewChild('scanner', { read: ElementRef }) private scannerElement?: ElementRef<HTMLElement>;
  @ViewChild('trailerScanner', { read: ElementRef }) private trailerScannerElement?: ElementRef<HTMLElement>;

  // Character code constants for numeric input validation
  private readonly CHAR_CODE_NINE: number = 57;
  private readonly CHAR_CODE_ZERO: number = 48;
  private readonly CONTROL_CHAR_MAX: number = 31;
  private destroyed$: Subject<void>;

  // Private component state
  private activeDialog?: MatDialogRef<PowerSupplyMessageDialog>;
  private activeRequests: number = 0;
  private formSubscriptionsSetup: boolean = false;
  private isComponentInitialized: boolean = false;
  private patchingTrailers: boolean = false;
  private selectedManufacturerName: string = '';
  private userName: string = '';
  private userpermissions: UserPermission[];

  public constructor(
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly commonService: CommonService,
    private readonly data: MessageService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly generatorService: GeneratorService,
    private readonly getHierarchyService: GetHierarchyService,
    private readonly location: Location,
    private readonly ngZone: NgZone,
    private readonly powerSupplyService: PowerSupplyService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.destroyed$ = new Subject<void>();
  }

  /**
   * Component initialization lifecycle hook.
   * Performs permission checks, initializes view model, loads dropdown data,
   * and sets up reactive forms and subscriptions.
   */
  public ngOnInit(): void {
    this.initializeViewModel();
    this.setupRouteParams();
    this.checkPermissions();
    this.loadAllDropdownData();
    this.initializeLocationForms();
    this.setupEditSaveSubscription();
  }

  /**
   * After view initialization lifecycle hook.
   * Sets up form status change subscriptions after Angular has fully initialized the view.
   */
  public ngAfterViewInit(): void {
    // Subscribe to form changes after Angular stabilizes
    this.ngZone.onStable
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.setupFormChangeSubscriptions();
      });
  }

  /**
   * Sets up subscriptions to form value/status changes for save button state management.
   * Guarded by formSubscriptionsSetup flag to prevent duplicate subscriptions.
   * Called from ngAfterViewInit and also after data patching as a fallback.
   */
  private setupFormChangeSubscriptions(): void {
    if (this.formSubscriptionsSetup) {
      return;
    }

    // Subscribe to generator form changes
    // Use debounceTime(0) to defer onChangeSave() to next macrotask,
    // ensuring ngModel has updated vm properties before validation runs.
    if (isDefined(this.generatorEditForm)) {
      this.generatorEditForm.statusChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());

      this.generatorEditForm.valueChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    // Subscribe to trailer form changes
    if (isDefined(this.trailerEditForm)) {
      this.trailerEditForm.statusChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());

      this.trailerEditForm.valueChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    // Subscribe to fuel tank form changes
    if (isDefined(this.fuelTankEditForm)) {
      this.fuelTankEditForm.statusChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());

      this.fuelTankEditForm.valueChanges
        ?.pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    // Subscribe to location form status changes
    if (isDefined(this.vm.generatorLocationInfoForm)) {
      this.vm.generatorLocationInfoForm.statusChanges
        .pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    if (isDefined(this.vm.trailerLocationInfoForm)) {
      this.vm.trailerLocationInfoForm.statusChanges
        .pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    if (isDefined(this.vm.fuelTankLocationInfoForm)) {
      this.vm.fuelTankLocationInfoForm.statusChanges
        .pipe(debounceTime(0), takeUntil(this.destroyed$))
        .subscribe((): void => this.onChangeSave());
    }

    this.formSubscriptionsSetup = true;

    // Set initial button state
    this.onChangeSave();

    // Mark component as initialized after all setup is complete
    this.isComponentInitialized = true;
  }

  /**
   * Component destruction lifecycle hook.
   * Properly cleans up subscriptions to prevent memory leaks.
   */
  public ngOnDestroy(): void {
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  /**
   * Reads route query parameters synchronously to set assetType early,
   * ensuring the correct form template is rendered before ngAfterViewInit.
   */
  private setupRouteParams(): void {
    const params: Record<string, string> = this.route.snapshot.queryParams;
    this.vm.queryParamsData = params;
    this.vm.assetId = params.qrCodeAssetId ?? '';
    this.vm.dbId = params.dbId ?? undefined;
    const assetType: string | undefined = params.assetType ?? undefined;
    this.vm.assetType = assetType
      ? `${assetType.charAt(0).toUpperCase()}${assetType.slice(1)}`
      : undefined;
    this.vm.generatorId = params.generatorId ?? undefined;
    this.vm.trailerId = params.trailerId ?? undefined;
    this.vm.fuelTankId = params.fuelTankId ?? undefined;
  }

  /**
   * Initializes the view model with default empty state.
   * All fields are set to appropriate empty values (empty strings, undefined, or empty arrays).
   */
  private initializeViewModel(): void {
    this.vm = this.createDefaultViewModel();
  }

  /**
   * Creates the default view model structure with all fields initialized.
   *
   * @returns {PowerSupplyGeneratorEditViewModel} The initialized view model
   */
  private createDefaultViewModel(): PowerSupplyGeneratorEditViewModel {
    return {
      ...this.getDefaultFuelTankFields(),
      ...this.getDefaultTrailerFields(),
      address: '',
      assetId: '',
      assetType: undefined,
      city: '',
      comments: '',
      currentGpsLatitude: '',
      currentGpsLongitude: '',
      currentTypes: [],
      dbId: undefined,
      divisions: [],
      generatorChain: '',
      generatorCord: '',
      generatorId: undefined,
      generatorLocationInfoForm: undefined,
      generatorLock: '',
      generatorTabIndex: 0,
      isModelLoading: false,
      isRegionsLoading: false,
      isTeamsLoading: false,
      isTrailerLoading: false,
      loanedToTeams: [],
      localId: '',
      maintenances: [],
      manufacturers: [],
      models: [],
      ownerContact: '',
      queryParamsData: {},
      regions: [],
      scannedValue: '',
      scannerEnabled: false,
      selectedCurrentType: '',
      selectedDivision: undefined,
      selectedDivisionObj: undefined,
      selectedLoanedToTeam: undefined,
      selectedMaintenance: undefined,
      selectedManufacturer: undefined,
      selectedManufacturerObj: undefined,
      selectedModel: undefined,
      selectedModelObj: undefined,
      selectedRegion: undefined,
      selectedRegionObj: undefined,
      selectedState: '',
      selectedStatus: undefined,
      selectedStatusObj: undefined,
      selectedTeam: undefined,
      selectedTrailer: undefined,
      selectedTrailerObj: undefined,
      selectedVoltage: '',
      serialNumber: '',
      showProgressSpinner: false,
      states: [],
      statuses: [],
      teams: [],
      voltageTypes: [],
      zipcode: '',
      zoomFlag: false,
      zoomLevel: 1
    };
  }

  private getDefaultFuelTankFields(): Pick<
    PowerSupplyGeneratorEditViewModel,
    'fuelTankAddress' |
    'fuelTankAssetId' |
    'fuelTankCapacities' |
    'fuelTankCity' |
    'fuelTankComments' |
    'fuelTankId' |
    'fuelTankLoanedToTeam' |
    'fuelTankLocalId' |
    'fuelTankLocationInfoForm' |
    'fuelTankScannedValue' |
    'fuelTankScannerEnabled' |
    'fuelTankSelectedCapacity' |
    'fuelTankSelectedDivision' |
    'fuelTankSelectedRegion' |
    'fuelTankSelectedStatus' |
    'fuelTankSelectedTeam' |
    'fuelTankSelectedTrailer' |
    'fuelTankSelectedTrailerName' |
    'fuelTankSerialNumber' |
    'fuelTankState' |
    'fuelTankTabIndex' |
    'fuelTankTeams' |
    'fuelTankZipcode' |
    'fuelTankZoomFlag' |
    'fuelTankZoomLevel'
  > {
    return {
      fuelTankAddress: '',
      fuelTankAssetId: '',
      fuelTankCapacities: [],
      fuelTankCity: '',
      fuelTankComments: '',
      fuelTankId: undefined,
      fuelTankLoanedToTeam: undefined,
      fuelTankLocalId: '',
      fuelTankLocationInfoForm: undefined,
      fuelTankScannedValue: '',
      fuelTankScannerEnabled: false,
      fuelTankSelectedCapacity: '',
      fuelTankSelectedDivision: undefined,
      fuelTankSelectedRegion: undefined,
      fuelTankSelectedStatus: undefined,
      fuelTankSelectedTeam: undefined,
      fuelTankSelectedTrailer: undefined,
      fuelTankSelectedTrailerName: '',
      fuelTankSerialNumber: '',
      fuelTankState: '',
      fuelTankTabIndex: 0,
      fuelTankTeams: [],
      fuelTankZipcode: '',
      fuelTankZoomFlag: false,
      fuelTankZoomLevel: 1
    };
  }

  private getDefaultTrailerFields(): Pick<
    PowerSupplyGeneratorEditViewModel,
    'trailerAssetId' |
    'trailerAwayFromHome' |
    'trailerCdlRequired' |
    'trailerCity' |
    'trailerComments' |
    'trailerGeneratorCapacity' |
    'trailerHeight' |
    'trailerHitchSize' |
    'trailerHitchType' |
    'trailerHouseNumber' |
    'trailerId' |
    'trailerLicensePlate' |
    'trailerLocalId' |
    'trailerLocationInfoForm' |
    'trailerLockCombination' |
    'trailerLockType' |
    'trailerName' |
    'trailerPrimaryContact' |
    'trailers' |
    'trailerScannedValue' |
    'trailerScannerEnabled' |
    'trailerSecondaryContact' |
    'trailerSelectedDivision' |
    'trailerSelectedLoanedToTeam' |
    'trailerSelectedRegion' |
    'trailerSelectedState' |
    'trailerSelectedStatus' |
    'trailerSerialNumber' |
    'trailerSize' |
    'trailerState' |
    'trailerStreet' |
    'trailerTabIndex' |
    'trailerZip' |
    'trailerZoomFlag' |
    'trailerZoomLevel'
  > {
    return {
      trailerAssetId: '',
      trailerAwayFromHome: undefined,
      trailerCdlRequired: undefined,
      trailerCity: '',
      trailerComments: '',
      trailerGeneratorCapacity: '',
      trailerHeight: '',
      trailerHitchSize: '',
      trailerHitchType: '',
      trailerHouseNumber: '',
      trailerId: undefined,
      trailerLicensePlate: '',
      trailerLocalId: '',
      trailerLocationInfoForm: undefined,
      trailerLockCombination: '',
      trailerLockType: '',
      trailerName: '',
      trailerPrimaryContact: '',
      trailers: [],
      trailerScannedValue: '',
      trailerScannerEnabled: false,
      trailerSecondaryContact: '',
      trailerSelectedDivision: undefined,
      trailerSelectedLoanedToTeam: undefined,
      trailerSelectedRegion: undefined,
      trailerSelectedState: '',
      trailerSelectedStatus: undefined,
      trailerSerialNumber: '',
      trailerSize: '',
      trailerState: '',
      trailerStreet: '',
      trailerTabIndex: 0,
      trailerZip: '',
      trailerZoomFlag: false,
      trailerZoomLevel: 1
    };
  }

  /**
   * Checks user permissions and redirects to unauthorized page if necessary.
   * Sets permission value for UI conditional rendering.
   */
  private checkPermissions(): void {
    const userPermissionsRaw: string | undefined = localStorage.getItem('user-permissions') ?? undefined;
    this.userpermissions = isDefinedNotNullNotEmpty(userPermissionsRaw) ? JSON.parse(userPermissionsRaw) : undefined;
    if (!this.authService.isAuthorizedForPowerSupply(this.userpermissions)) {
      this.router.navigate(['/unauthorized']);
      return;
    }

    this.userName = `${this.authService.getUserDetails.firstName} ${this.authService.getUserDetails.lastName}`;
  }

  /**
   * Loads all dropdown data using forkJoin to ensure all data is loaded before patching asset data.
   * Called during component initialization to populate select fields.
   */
  private loadAllDropdownData(): void {
    this.startLoading();
    forkJoin({
      currentTypes: this.generatorService.getCurrentTypesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<CurrentTypeDTO[]> => of([]))
      ),
      divisions: this.getHierarchyService.getChildren({ elementID: 1 })?.pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<JsonHierarchyElement[]> => of([]))
      ) ?? of([]),
      fuelTankSizes: this.generatorService.getFuelTankSizesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<FuelTankSizeDTO[]> => of([]))
      ),
      loanedToTeams: this.generatorService.getAllLoanedNWTTeams().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<string[]> => of([]))
      ),
      maintenances: this.generatorService.getMaintenanceTypesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<MaintenanceTypeDTO[]> => of([]))
      ),
      manufacturers: this.generatorService.getManufactureTypesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<ManufactureTypeDTO[]> => of([]))
      ),
      states: this.generatorService.getStatesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<StatesEntity[]> => of([]))
      ),
      statuses: this.generatorService.getStatusTypesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<StatusTypeDTO[]> => of([]))
      ),
      voltageTypes: this.generatorService.getVoltageTypesService().pipe(
        take(1),
        takeUntil(this.destroyed$),
        catchError((): Observable<VoltageTypeDTO[]> => of([]))
      )
    }).pipe(
      finalize((): void => this.stopLoading())
    ).subscribe({
      error: (): void => {
        this.openMessageDialog('Failed to load dropdown data. Some fields may be empty.');
      },
      next: (result: {
        currentTypes?: CurrentTypeDTO[];
        divisions?: JsonHierarchyElement[];
        fuelTankSizes?: FuelTankSizeDTO[];
        loanedToTeams?: string[];
        maintenances?: MaintenanceTypeDTO[];
        manufacturers?: ManufactureTypeDTO[];
        states?: StatesEntity[];
        statuses?: StatusTypeDTO[];
        voltageTypes?: VoltageTypeDTO[];
      }): void => {
        // Assign all loaded data to view model
        this.vm.statuses = result.statuses ?? [];
        this.vm.manufacturers = result.manufacturers ?? [];
        this.vm.currentTypes = result.currentTypes ?? [];
        this.vm.fuelTankCapacities = result.fuelTankSizes ?? [];
        this.vm.voltageTypes = result.voltageTypes ?? [];
        this.vm.loanedToTeams = result.loanedToTeams ?? [];
        this.vm.divisions = (result.divisions ?? []).filter(
          (division: JsonHierarchyElement): boolean => division.name !== 'National Test' && division.name !== 'National'
        );
        this.vm.maintenances = result.maintenances ?? [];
        this.vm.states = result.states ?? [];

        this.cdr.detectChanges();

        // NOW load asset data after all dropdowns are populated
        this.loadQueryParamsAndAssetData();
      }
    });
  }

  /**
   * Loads query parameters and fetches asset data based on the asset type.
   * Patches the view model with existing asset data for editing.
   */
  private loadQueryParamsAndAssetData(): void {
    const type: string = (this.vm.assetType ?? '').toLowerCase();

    // Load asset data based on type (params already set in setupRouteParams)
    switch (type) {
      case 'generator': {
        const id: string | undefined = this.vm.generatorId ?? this.vm.dbId;
        if (isDefinedNotNullNotEmpty(id)) {
          this.loadGeneratorData(id);
        }
        break;
      }
      case 'fueltank': {
        const id: string | undefined = this.vm.fuelTankId ?? this.vm.dbId;
        if (isDefinedNotNullNotEmpty(id)) {
          this.loadFuelTankData(id);
        }
        break;
      }
      case 'trailer': {
        const id: string | undefined = this.vm.trailerId ?? this.vm.dbId;
        if (isDefinedNotNullNotEmpty(id)) {
          this.loadTrailerData(id);
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Loads generator data by ID and patches the view model.
   *
   * @param {string} id The generator ID to load
   */
  private loadGeneratorData(id: string): void {
    this.startLoading();
    this.generatorService.generatorSearchByAssetId(id)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((generatorData: GeneratorSearchDTO[]): void => {
        const generator: GeneratorSearchDTO | undefined =
          isDefinedNotNullNotEmpty(generatorData)
            ? generatorData[0]
            : undefined;
        if (isDefined(generator)) {
          this.patchGenerator(generator);
        }
      });
  }

  /**
   * Loads fuel tank data by ID and patches the view model.
   *
   * @param {string} id The fuel tank ID to load
   */
  private loadFuelTankData(id: string): void {
    this.startLoading();
    this.generatorService.fuelTankSearchById(id)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((fuelTankData: FuelTankEntity[]): void => {
        const fuelTank: FuelTankEntity | undefined =
          isDefinedNotNullNotEmpty(fuelTankData)
            ? fuelTankData[0]
            : undefined;
        if (isDefined(fuelTank)) {
          this.patchFuelTank(fuelTank);
        }
      });
  }

  /**
   * Loads trailer data by ID and patches the view model.
   *
   * @param {string} id The trailer ID to load
   */
  private loadTrailerData(id: string): void {
    this.startLoading();
    this.generatorService.trailerSearchById(id)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((trailerData: TrailerEntity[]): void => {
        const trailer: TrailerEntity | undefined =
          isDefinedNotNullNotEmpty(trailerData)
            ? trailerData[0]
            : undefined;
        if (isDefined(trailer)) {
          this.patchTrailer(trailer);
        }
      });
  }

  /**
   * Patches generator data into the view model.
   * Maps API response fields to view model properties and handles nested data structures.
   *
   * @param {GeneratorSearchDTO} generator The generator data from the API
   */
  private patchGenerator(generator: GeneratorSearchDTO): void {
    const generatorPatch: GeneratorSearchDTO = generator;
    this.patchGeneratorBasicFields(generatorPatch);
    this.patchGeneratorStatus(generatorPatch);
    this.patchGeneratorManufacturerModel(generatorPatch);
    this.patchGeneratorVoltage(generatorPatch);
    this.patchGeneratorDivisionRegionTeam(generatorPatch);
    this.patchGeneratorMaintenance(generatorPatch);
    this.patchGeneratorGps(generatorPatch);
    this.cdr.detectChanges();
    this.finalizePatch();
  }

  private patchGeneratorBasicFields(generatorPatch: GeneratorSearchDTO): void {
    this.vm.assetId = String(generatorPatch.qrCodeId ?? '');
    this.vm.localId = String(generatorPatch.localId ?? '');
    this.vm.serialNumber = String(generatorPatch.generatorSerialNumber ?? '');
    this.vm.address = String(generatorPatch.address ?? '');
    this.vm.city = String(generatorPatch.city ?? '');
    this.vm.selectedState = String(generatorPatch.state ?? '');
    this.vm.selectedLoanedToTeam = String(generatorPatch.loanedTeamName ?? '');
    this.vm.zipcode = String(generatorPatch.zipcode ?? '');
    this.vm.generatorCord = String(generatorPatch.generatorCord ?? '');
    this.vm.generatorChain = String(generatorPatch.generatorChain ?? '');
    this.vm.generatorLock = String(generatorPatch.generatorLock ?? '');
    this.vm.ownerContact = String(generatorPatch.ownerContact ?? '');
    this.vm.comments = String(generatorPatch.comments ?? '');
    this.vm.selectedCurrentType = String(generatorPatch.currentType ?? '');
  }

  private patchGeneratorStatus(generatorPatch: GeneratorSearchDTO): void {
    const statusId: number | undefined = this.extractNumeric(
      generatorPatch.generatorStatusId ?? generatorPatch.generatorStatus);
    if (isDefined(statusId)) {
      const statusMatch: StatusTypeDTO | undefined = this.vm.statuses.find(
        (statusData: StatusTypeDTO): boolean => statusData.id === statusId
      );
      if (isDefined(statusMatch)) {
        this.vm.selectedStatus = statusMatch.id;
      }
    }
  }

  private patchGeneratorManufacturerModel(generatorPatch: GeneratorSearchDTO): void {
    if (isUndefined(generatorPatch.generatorManufacturer)) {
      return;
    }
    const manufacturer: ManufactureTypeDTO | undefined = this.vm.manufacturers.find(
      (manufactureData: ManufactureTypeDTO): boolean =>
        (manufactureData.manufacture ?? '').toLowerCase() === String(generatorPatch.generatorManufacturer).toLowerCase()
    );
    if (isUndefined(manufacturer?.manufacture)) {
      return;
    }
    this.vm.selectedManufacturer = manufacturer.id;
    this.selectedManufacturerName = manufacturer.manufacture;

    this.loadModels(manufacturer.manufacture, (): void => {
      if (isDefined(generatorPatch.generatorModel)) {
        const model: ModelTypeDTO | undefined = this.vm.models.find(
          (modelTypeData: ModelTypeDTO): boolean =>
            (modelTypeData.genModel ?? '').toLowerCase() === String(generatorPatch.generatorModel).toLowerCase()
        );
        if (isDefined(model)) {
          this.vm.selectedModel = model.id;
        }
      }
      this.cdr.detectChanges();
      if (this.isComponentInitialized) {
        this.onChangeSave();
      }
    });
  }

  private patchGeneratorVoltage(generatorPatch: GeneratorSearchDTO): void {
    const voltageValue: string | undefined = generatorPatch.outputVoltage;
    if (isUndefined(voltageValue)) {
      this.vm.selectedVoltage = '';
      return;
    }
    const voltageNum: number = typeof voltageValue === 'number' ? voltageValue : parseFloat(String(voltageValue));
    if (isUndefinedOrNullOrEmpty(voltageNum)) {
      this.vm.selectedVoltage = '';
      return;
    }
    const voltageMatch: VoltageTypeDTO | undefined = this.vm.voltageTypes.find(
      (voltageData: VoltageTypeDTO): boolean => voltageData.voltage === voltageNum
    );
    this.vm.selectedVoltage = isDefined(voltageMatch?.voltage) ? voltageMatch.voltage : voltageNum;
  }

  private patchGeneratorDivisionRegionTeam(generatorPatch: GeneratorSearchDTO): void {
    const divisionToUse: JsonHierarchyElement | undefined = this.resolveDivision(generatorPatch.division);
    if (isUndefinedOrNull(divisionToUse?.elementID)) {
      return;
    }
    this.vm.selectedDivision = divisionToUse.elementID;

    this.loadRegions(divisionToUse.elementID, (): void => {
      const regionToUse: JsonHierarchyElement | undefined = this.resolveRegion(generatorPatch.region);
      if (isDefinedNotNull(regionToUse?.elementID)) {
        this.vm.selectedRegion = regionToUse.elementID;
        this.loadTeams(regionToUse.elementID, (): void => {
          this.patchTeams(generatorPatch);
        });
      } else {
        this.cdr.detectChanges();
        if (this.isComponentInitialized) {
          this.onChangeSave();
        }
      }
    });
  }

  private patchGeneratorMaintenance(generatorPatch: GeneratorSearchDTO): void {
    if (isUndefinedOrNull(generatorPatch.maintenance)) {
      return;
    }
    const maintenanceName: string =
      (generatorPatch.maintenance ?? '').toString().trim();
    const maintenanceMatch: MaintenanceTypeDTO | undefined = this.vm.maintenances.find(
      (maintenanceType: MaintenanceTypeDTO): boolean =>
        maintenanceType.maintenance?.toLowerCase() === maintenanceName.toLowerCase()
    );
    this.vm.selectedMaintenance = isDefined(maintenanceMatch) ? maintenanceMatch.maintenance : maintenanceName;
  }

  private patchGeneratorGps(generatorPatch: GeneratorSearchDTO): void {
    const gpsLat: string = String(
      generatorPatch.latitude ?? ''
    );
    const gpsLng: string = String(
      generatorPatch.longitude ?? ''
    );
    if (isDefined(this.vm.generatorLocationInfoForm)) {
      this.vm.generatorLocationInfoForm.patchValue({
        currentGpsLatitude: gpsLat,
        currentGpsLongitude: gpsLng
      });
    }
    this.vm.currentGpsLatitude = gpsLat;
    this.vm.currentGpsLongitude = gpsLng;
  }

  /**
   * Patches fuel tank data into the view model.
   *
   * @param {FuelTankEntity} fuelTank The fuel tank data from the API
   */
  private patchFuelTank(fuelTank: FuelTankEntity): void {
    const fuelTankPatch: FuelTankEntity = fuelTank;
    this.patchFuelTankBasicFields(fuelTankPatch);
    this.vm.fuelTankSelectedStatus = this.resolveStatusId(fuelTankPatch.status);
    this.vm.fuelTankSelectedCapacity = String(fuelTankPatch.capacity ?? '');
    this.ensureFuelTankCapacityInList(this.vm.fuelTankSelectedCapacity);
    this.patchFuelTankDivisionRegionTeam(fuelTankPatch);
    this.patchFuelTankGps(fuelTankPatch);
    this.cdr.detectChanges();
    this.finalizePatch();
  }

  private patchFuelTankBasicFields(fuelTankPatch: FuelTankEntity): void {
    this.vm.fuelTankAssetId = String(fuelTankPatch.qrCodeId ?? '');
    this.vm.fuelTankLocalId = String(fuelTankPatch.localId ?? '');
    this.vm.fuelTankSerialNumber = String(fuelTankPatch.serialNumber ?? '');
    this.vm.fuelTankComments = String(fuelTankPatch.comments ?? '');
    this.vm.fuelTankAddress = String(fuelTankPatch.address ?? '');
    this.vm.fuelTankCity = String(fuelTankPatch.city ?? '');
    this.vm.fuelTankState = String(fuelTankPatch.state ?? '');
    this.vm.fuelTankLoanedToTeam = String(fuelTankPatch.loanedToTeam ?? '');
    this.vm.fuelTankZipcode = String(fuelTankPatch.zipCode ?? '');
  }

  private patchFuelTankDivisionRegionTeam(fuelTankPatch: FuelTankEntity): void {
    const divisionToUse: JsonHierarchyElement | undefined = this.resolveDivision(fuelTankPatch.division);
    if (isUndefinedOrNull(divisionToUse?.elementID)) {
      return;
    }
    this.vm.fuelTankSelectedDivision = divisionToUse.elementID;

    this.loadRegions(divisionToUse.elementID, (): void => {
      const regionToUse: JsonHierarchyElement | undefined = this.resolveRegion(fuelTankPatch.region);
      if (isDefinedNotNull(regionToUse?.elementID)) {
        this.vm.fuelTankSelectedRegion = regionToUse.elementID;
        this.loadTeams(regionToUse.elementID, (): void => {
          this.patchFuelTankTeams(fuelTankPatch);
        });
      } else {
        this.cdr.detectChanges();
        if (this.isComponentInitialized) {
          this.onChangeSave();
        }
      }
    });
  }

  private patchFuelTankGps(fuelTankPatch: FuelTankEntity): void {
    const gpsLat: string = String(fuelTankPatch.latitude ?? '');
    const gpsLng: string = String(fuelTankPatch.longitude ?? '');
    if (isDefined(this.vm.fuelTankLocationInfoForm)) {
      this.vm.fuelTankLocationInfoForm.patchValue({
        currentGpsLatitude: gpsLat,
        currentGpsLongitude: gpsLng
      });
    }
  }

  /**
   * Patches trailer data into the view model.
   *
   * @param {TrailerEntity} trailer The trailer data from the API
   */
  private patchTrailer(trailer: TrailerEntity): void {
    const trailerPatch: TrailerEntity = trailer;
    this.patchTrailerBasicFields(trailerPatch);
    this.vm.trailerSelectedStatus = this.resolveStatusId(trailerPatch.status);
    this.patchTrailerDivisionRegionTeam(trailerPatch);
    this.patchTrailerGps(trailerPatch);
    this.cdr.detectChanges();
    this.finalizePatch();
  }

  // Complexity cannot be reduced: each assignment patches a distinct trailer field from the API response.
  // Breaking this into smaller methods would add unnecessary indirection without improving readability.
  // eslint-disable-next-line complexity
  private patchTrailerBasicFields(trailerPatch: TrailerEntity): void {
    this.vm.trailerAssetId = String(trailerPatch.qrCodeId ?? '');
    this.vm.trailerLocalId = String(trailerPatch.localId ?? '');
    this.vm.trailerSerialNumber = String(trailerPatch.serialNumber ?? '');
    this.vm.trailerName = String(trailerPatch.trailerName ?? '');
    this.vm.trailerSize = String(trailerPatch.trailerSize ?? '');
    this.vm.trailerHeight = String(trailerPatch.trailerHeight ?? '');
    this.vm.trailerComments = String(trailerPatch.comments ?? '');
    this.vm.trailerAwayFromHome = this.normalizeFlag(trailerPatch.awayFromHome);
    this.vm.trailerCdlRequired = this.normalizeFlag(trailerPatch.cdlRequired);
    this.vm.trailerGeneratorCapacity = String(trailerPatch.generatorCapacity ?? '');
    this.vm.trailerHitchSize = String(trailerPatch.hitchSize ?? '');
    this.vm.trailerHitchType = String(trailerPatch.hitchType ?? '');
    this.vm.trailerLicensePlate = String(trailerPatch.licensePlate ?? '');
    this.vm.trailerLockCombination = String(trailerPatch.lockCombination ?? '');
    this.vm.trailerLockType = String(trailerPatch.lockType ?? '');
    this.vm.trailerPrimaryContact = String(trailerPatch.primaryContact ?? '');
    this.vm.trailerSecondaryContact = String(trailerPatch.secondaryContact ?? '');
    this.vm.trailerSelectedLoanedToTeam = String(trailerPatch.trailerLoanedTeamId ?? '');
    this.vm.trailerCity = String(trailerPatch.city ?? '');
    this.vm.trailerHouseNumber = String(trailerPatch.houseNumber ?? '');
    this.vm.trailerSelectedState = String(trailerPatch.state ?? '');
    this.vm.trailerStreet = String(trailerPatch.street ?? '');
    this.vm.trailerZip = String(trailerPatch.zipCode ?? '');
  }

  private patchTrailerDivisionRegionTeam(trailerPatch: TrailerEntity): void {
    const divisionToUse: JsonHierarchyElement | undefined = this.resolveDivision(trailerPatch.division);
    if (isUndefinedOrNull(divisionToUse?.elementID)) {
      return;
    }
    this.vm.trailerSelectedDivision = divisionToUse.elementID;

    this.loadRegions(divisionToUse.elementID, (): void => {
      const regionToUse: JsonHierarchyElement | undefined = this.resolveRegion(trailerPatch.region);
      if (isDefinedNotNull(regionToUse?.elementID)) {
        this.vm.trailerSelectedRegion = regionToUse.elementID;
        this.loadTeams(regionToUse.elementID, (): void => {
          if (isDefined(trailerPatch.teamName)) {
            this.vm.selectedTeam = String(trailerPatch.teamName);
          }
          this.cdr.detectChanges();
          if (this.isComponentInitialized) {
            this.onChangeSave();
          }
        });
      } else {
        this.cdr.detectChanges();
        if (this.isComponentInitialized) {
          this.onChangeSave();
        }
      }
    });
  }

  private patchTrailerGps(trailerPatch: TrailerEntity): void {
    const gpsLat: string = String(trailerPatch.currentLatitude ?? '');
    const gpsLng: string = String(trailerPatch.currentLongitude ?? '');
    if (isDefined(this.vm.trailerLocationInfoForm)) {
      this.vm.trailerLocationInfoForm.patchValue({
        currentGpsLatitude: gpsLat,
        currentGpsLongitude: gpsLng
      });
    }
  }

  /**
   * Normalizes flag-like values into numeric 1/0 or undefined.
   *
   * @param {boolean | number | string | undefined} val The value to normalize
   * @returns {number | undefined} 1, 0, or undefined
   */
  private normalizeFlag(val: boolean | number | string | undefined): number | undefined {
    if (val === true || val === 'true' || val === 1 || val === '1') {
      return 1;
    }
    if (val === false || val === 'false' || val === 0 || val === '0') {
      return 0;
    }
    return undefined;
  }

  /**
   * Extracts a numeric value from various input types.
   *
   * @param {unknown} value The value to extract
   * @returns {number | undefined} The numeric value or undefined
   */
  private extractNumeric(value: string | number | undefined | unknown): number | undefined {
    if (isUndefined(value)) {
      return undefined;
    }
    if (typeof value === 'number') {
      return value;
    }
    const parsed: number = parseInt(String(value), 10);
    return isNaN(parsed) ? undefined : parsed;
  }

  /**
   * Resolves a division from the loaded divisions list by numeric ID or name.
   */
  private resolveDivision(value: string | number | undefined | unknown): JsonHierarchyElement | undefined {
    const numericId: number | undefined = this.extractNumeric(value);
    return numericId
      ? this.vm.divisions.find(
          (divisionValue: JsonHierarchyElement): boolean => divisionValue.elementID === numericId
        )
      : this.vm.divisions.find(
          (divisionValue: JsonHierarchyElement): boolean =>
            divisionValue.name?.toLowerCase() === String(value).toLowerCase()
        );
  }

  /**
   * Resolves a region from the loaded regions list by numeric ID or name.
   */
  private resolveRegion(value: string | number | undefined): JsonHierarchyElement | undefined {
    const numericId: number | undefined = this.extractNumeric(value);
    return numericId
      ? this.vm.regions.find(
          (regionData: JsonHierarchyElement): boolean => regionData.elementID === numericId
        )
      : this.vm.regions.find(
          (regionData: JsonHierarchyElement): boolean =>
            regionData.name?.toLowerCase() === String(value).toLowerCase()
        );
  }

  /**
   * Resolves a status ID from the loaded statuses list by numeric ID or status name.
   */
  private resolveStatusId(statusValue: string | number | undefined): number | undefined {
    const statusId: number | undefined = this.extractNumeric(statusValue);
    if (isDefined(statusId)) {
      const match: StatusTypeDTO | undefined = this.vm.statuses.find(
        (status: StatusTypeDTO): boolean => status.id === statusId
      );
      if (isDefined(match)) {
        return match.id;
      }
    }
    const byName: StatusTypeDTO | undefined = this.vm.statuses.find(
      (status: StatusTypeDTO): boolean =>
        status.statusType?.toLowerCase() === String(statusValue ?? '').toLowerCase()
    );
    return byName?.id;
  }

  /**
   * Runs common finalization logic after patching asset data into the view model.
   */
  private finalizePatch(): void {
    if (!this.formSubscriptionsSetup) {
      setTimeout((): void => this.setupFormChangeSubscriptions());
    }
    if (this.isComponentInitialized) {
      this.onChangeSave();
    }
  }

  /**
   * Ensures that a fuel tank capacity value exists in the dropdown list.
   * If the capacity doesn't exist, it adds it to prevent display issues.
   *
   * @param {string} capacity The capacity value to ensure exists in the list
   */
  private ensureFuelTankCapacityInList(capacity?: string): void {
    if (isDefinedNotNullNotEmpty(capacity)) {
      const exists: boolean = this.vm.fuelTankCapacities.some(
        (capacityData: FuelTankSizeDTO): boolean => capacityData.fuelTankSize === capacity
      );
      if (!exists) {
        this.vm.fuelTankCapacities.push({
          fuelTankSize: capacity,
          id: undefined
        });
        this.cdr.detectChanges();
      }
    }
  }

  /**
   * Initializes reactive forms for GPS validation on all three asset types.
   * Uses custom GPS validators for latitude and longitude fields.
   */
  private initializeLocationForms(): void {
    // Initialize generator location form
    this.vm.generatorLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.generatorLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.generatorLocationInfoForm);
      });

    // Initialize fuel tank location form
    this.vm.fuelTankLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.fuelTankLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.fuelTankLocationInfoForm);
      });

    // Initialize trailer location form
    this.vm.trailerLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.trailerLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.trailerLocationInfoForm);
      });
  }

  /**
   * Sets up subscription to MessageService for save button clicks.
   * Triggered when the user clicks the save button in the parent component.
   */
  private setupEditSaveSubscription(): void {
    this.data.messageGeneratorEditSave$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data: { isGeneratorEditSubmit?: boolean }): void => {
        if (!this.isComponentInitialized) {
          return;
        }

        if (data.isGeneratorEditSubmit) {
          const assetType: string | undefined = this.vm.assetType;
          switch (assetType) {
            case 'Generator':
              this.updateGenerator();
              break;
            case 'Trailer':
              this.updateTrailer();
              break;
            case 'Fueltank':
              this.updateFuelTank();
              break;
            default:
              break;
          }
          // Reset the submit flag after processing
          this.data.generatorEditSaveMessage('isGeneratorEditSubmit', false);
        }
      });
  }

  /**
   * Loads models for a specific manufacturer.
   *
   * @param {string} manufacturerName The manufacturer name
   * @param {() => void} callback Optional callback to execute after models are loaded
   */
  private loadModels(manufacturerName: string, callback?: () => void): void {
    this.vm.isModelLoading = true;
    this.startLoading();

    this.generatorService.getModelsByManufacturer(manufacturerName)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isModelLoading = false;
          this.stopLoading();
          if (isDefined(callback)) {
            callback();
          }
        })
      )
      .subscribe((models: ModelTypeDTO[]): void => {
        this.vm.models = models ?? [];
        this.cdr.detectChanges();
      });
  }

  /**
   * Loads regions for a specific division.
   *
   * @param {number} divisionId The division ID
   * @param {() => void} callback Optional callback to execute after regions are loaded
   */
  private loadRegions(divisionId: number, callback?: () => void): void {
    this.vm.isRegionsLoading = true;
    this.startLoading();

    this.getHierarchyService.getChildren({ elementID: divisionId })
      ?.pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isRegionsLoading = false;
          this.stopLoading();
          if (isDefined(callback)) {
            callback();
          }
        })
      )
      .subscribe((results: JsonHierarchyElement[]): void => {
        this.vm.regions = results ?? [];
        this.cdr.detectChanges();
      });
  }

  /**
   * Loads teams for a specific region.
   *
   * @param {number} regionId The region ID
   * @param {() => void} callback Optional callback to execute after teams are loaded
   */
  private loadTeams(regionId: number, callback?: () => void): void {
    this.vm.isTeamsLoading = true;
    this.startLoading();

    this.powerSupplyService.getNWTTeamsList([regionId])
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isTeamsLoading = false;
          this.stopLoading();
          if (isDefined(callback)) {
            callback();
          }
        })
      )
      .subscribe((results: string[]): void => {
        const raw: string[] = results ?? [];
        const teamNames: string[] = raw.map((teams: string): string => {
          if (typeof teams === 'string') {
            return teams;
          }
          const team: Record<string, unknown> = teams;
          return String(team.teamName ?? team.name ?? team.team_id ?? team.id ?? '');
        }).filter((filteredName: string): boolean => !isEmpty(filteredName));

        // Remove duplicates (case-insensitive)
        const seen: Record<string, string> = {};
        this.vm.teams = teamNames.filter((name: string): boolean => {
          const key: string = name.toLowerCase();
          if (isDefined(seen[key])) {
            return false;
          }
          seen[key] = name;
          return true;
        });

        this.cdr.detectChanges();
      });
  }

  /**
   * Loads trailers for a specific team (for generators).
   *
   * @param {string} teamName The team name
   * @param {number | undefined} trailerIdToSelect The trailer ID to select after loading
   */
  private loadTrailers(teamName: string, trailerIdToSelect?: number | undefined): void {
    this.vm.isTrailerLoading = true;
    this.startLoading();

    this.generatorService.getTrailersByOwnerTeam(teamName)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isTrailerLoading = false;
          this.patchingTrailers = false;
          this.stopLoading();
          // Update button state after trailers are loaded
          if (this.isComponentInitialized) {
            this.onChangeSave();
          }
        })
      )
      .subscribe((trailers: TrailerNameIdDTO[]): void => {
        this.vm.trailers = trailers ?? [];

        // Select trailer if trailerIdToSelect was provided
        if (isDefined(trailerIdToSelect) && !isEmpty(this.vm.trailers)) {
          const found: TrailerNameIdDTO | undefined = this.vm.trailers.find(
            (trailerData: TrailerNameIdDTO): boolean => trailerData.trailerId === trailerIdToSelect
          );
          this.vm.selectedTrailer = isDefined(found) ? found.trailerId : trailerIdToSelect;
        }

        this.cdr.detectChanges();
      });
  }

  /**
   * Loads trailers for a specific team (for fuel tanks).
   *
   * @param {string} teamName The team name
   * @param {number | undefined} trailerIdToSelect The trailer ID to select after loading
   */
  private loadTrailersForFuelTank(teamName: string, trailerIdToSelect?: number | undefined): void {
    this.vm.isTrailerLoading = true;
    this.cdr.detectChanges();
    this.startLoading();

    this.generatorService.getTrailersByOwnerTeam(teamName)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isTrailerLoading = false;
          this.patchingTrailers = false;
          this.stopLoading();
          // Update button state after trailers are loaded
          if (this.isComponentInitialized) {
            this.onChangeSave();
          }
        })
      )
      .subscribe((trailers: TrailerNameIdDTO[]): void => {
        this.vm.trailers = trailers ?? [];

        // Select trailer if trailerIdToSelect was provided
        if (isDefined(trailerIdToSelect) && !isEmpty(this.vm.trailers)) {
          const found: TrailerNameIdDTO | undefined = this.vm.trailers.find(
            (trailerData: TrailerNameIdDTO): boolean => trailerData.trailerId === trailerIdToSelect
          );
          this.vm.fuelTankSelectedTrailer = isDefined(found) ? found.trailerId : trailerIdToSelect;
          if (isDefined(found)) {
            this.vm.fuelTankSelectedTrailerName = found.trailerName ?? '';
          }
        }

        this.cdr.detectChanges();
      });
  }

  /**
   * Handles status selection change and updates the selected status values.
   *
   * @param {number} statusId The selected status ID
   */
  public onStatusChange(statusId: number): void {
    if (isDefined(statusId)) {
      this.vm.selectedStatus = statusId;
     this.cdr.detectChanges();
    }
    this.onChangeSave();
  }

  /**
   * Handles manufacturer selection change, updates manufacturer values, and loads associated models.
   *
   * @param {number} manufactureId The selected manufacturer ID
   */
  public onManufacturerChange(manufactureId: number): void {
    if (isDefined(manufactureId)) {
      this.vm.selectedManufacturer = manufactureId;
      const manufacturer: ManufactureTypeDTO | undefined = this.vm.manufacturers.find(
        (manufacturerData: ManufactureTypeDTO): boolean => manufacturerData.id === manufactureId
      );
      this.selectedManufacturerName = manufacturer?.manufacture ?? '';

      // Reset model selection
      this.vm.models = [];
      this.vm.selectedModel = undefined;

      // Load models for the selected manufacturer
      if (isDefined(manufacturer)) {
        this.loadModels(this.selectedManufacturerName);
      }
    } else {
      this.vm.selectedManufacturer = undefined;
      this.selectedManufacturerName = '';
      this.vm.models = [];
      this.vm.selectedModel = undefined;
    }
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles model selection change and updates the selected model values.
   *
   * @param {number} modelId The selected model ID
   */
  public onModelChange(modelId: number): void {
    if (isDefined(modelId)) {
      this.vm.selectedModel = modelId;
    } else {
      this.vm.selectedModel = undefined;
    }
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles division selection change, clears dependent fields, and loads associated regions.
   *
   * @param {number} divisionId The selected division ID
   */
  public onDivisionChange(divisionId: number): void {
    if (isDefined(divisionId)) {
      this.vm.selectedDivision = divisionId;

      // Load regions for this division
      this.loadRegions(divisionId);
    } else {
      this.vm.selectedDivision = undefined;
    }
    // Clear dependent fields
    this.vm.regions = [];
    this.vm.selectedRegion = undefined;
    this.vm.teams = [];
    this.vm.selectedTeam = undefined;
    this.vm.trailers = [];
    this.vm.selectedTrailer = undefined;
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles region selection change, clears dependent fields, and loads associated teams.
   *
   * @param {number} regionId The selected region ID
   */
  public onRegionChange(regionId: number): void {
    if (isDefined(regionId)) {
      this.vm.selectedRegion = regionId;

      // Load teams for this region
      this.loadTeams(regionId);
    } else {
      this.vm.selectedRegion = undefined;
    }
    // Clear dependent fields
    this.vm.teams = [];
    this.vm.selectedTeam = undefined;
    this.vm.trailers = [];
    this.vm.selectedTrailer = undefined;
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles team selection change and loads associated trailers.
   *
   * @param {string} team The selected team name
   */
  public onTeamChange(team: string): void {
    if (isDefined(team) && this.vm.assetType === 'Generator') {
      this.vm.selectedTeam = team;
      // Clear trailer selection when user changes team (not during patching)
      if (!this.patchingTrailers) {
        this.vm.selectedTrailer = undefined;
      }
      this.vm.trailers = [];
      this.loadTrailers(team);
    } else if (!isDefined(team)) {
      this.vm.selectedTeam = undefined;
      this.vm.trailers = [];
      this.vm.selectedTrailer = undefined;
    }
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Patches teams for generator during initial data load.
   * Assigns team name and loads trailers with proper selection.
   *
   * @param {GeneratorSearchDTO} generator The generator data
   */
  private patchTeams(generator: GeneratorSearchDTO): void {
    const generatorTeam: GeneratorSearchDTO = generator;
    const teamName: string = (generatorTeam.teamName ?? '').toString().trim();
    if (isUndefinedOrNullOrEmpty(teamName)) {
      if (this.isComponentInitialized) {
        this.onChangeSave();
      }
      return;
    }

    // Assign team name
    this.vm.selectedTeam = teamName;
    this.cdr.detectChanges();

    // Extract trailer ID to select after trailers load
    const trailerId: number | undefined = this.extractNumeric(generatorTeam.trailerId);

    if (isDefinedNotNullNotEmpty(this.vm.selectedTeam)) {
      this.patchingTrailers = true;
      this.loadTrailers(this.vm.selectedTeam, trailerId);
    }
  }

  /**
   * Patches teams for fuel tank during initial data load.
   * Assigns team name and loads trailers with proper selection.
   *
   * @param {FuelTankEntity} fuelTank The fuel tank data
   */
  private patchFuelTankTeams(fuelTank: FuelTankEntity): void {
    const fuelTankTeams: FuelTankEntity = fuelTank;
    const teamName: string = (fuelTankTeams.teamName ?? '').toString().trim();
    if (isUndefinedOrNullOrEmpty(teamName)) {
      if (this.isComponentInitialized) {
        this.onChangeSave();
      }
      return;
    }

    // Assign team name
    this.vm.fuelTankSelectedTeam = teamName;
    this.cdr.detectChanges();

    // Extract trailer ID and name to select after trailers load
    const trailerId: number | undefined = this.extractNumeric(fuelTankTeams.trailerId);

    if (isDefinedNotNullNotEmpty(this.vm.fuelTankSelectedTeam)) {
      this.patchingTrailers = true;
      this.loadTrailersForFuelTank(this.vm.fuelTankSelectedTeam, trailerId);
    }
  }

  /**
   * Handles fuel tank division selection change.
   *
   * @param {number} divisionId The selected division ID
   */
  public onFuelTankDivisionChange(divisionId: number): void {
    if (isDefined(divisionId)) {
      this.vm.fuelTankSelectedDivision = divisionId;
      this.loadRegions(divisionId);
    } else {
      this.vm.fuelTankSelectedDivision = undefined;
    }
    // Clear all dependent fields
    this.vm.regions = [];
    this.vm.fuelTankSelectedRegion = undefined;
    this.vm.teams = [];
    this.vm.fuelTankTeams = [];
    this.vm.fuelTankSelectedTeam = undefined;
    this.vm.fuelTankSelectedTrailer = undefined;
    this.vm.fuelTankSelectedTrailerName = '';
    this.vm.trailers = [];
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles fuel tank region selection change.
   *
   * @param {number} regionId The selected region ID
   */
  public onFuelTankRegionChange(regionId: number): void {
    if (isDefined(regionId)) {
      this.vm.fuelTankSelectedRegion = regionId;
      this.loadTeams(regionId);
    } else {
      this.vm.fuelTankSelectedRegion = undefined;
    }
    // Clear all dependent fields
    this.vm.teams = [];
    this.vm.fuelTankTeams = [];
    this.vm.fuelTankSelectedTeam = undefined;
    this.vm.fuelTankSelectedTrailer = undefined;
    this.vm.fuelTankSelectedTrailerName = '';
    this.vm.trailers = [];
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles fuel tank team selection change.
   *
   * @param {string} team The selected team name
   */
  public onFuelTankTeamChange(team: string): void {
    if (isDefined(team)) {
      this.vm.fuelTankSelectedTeam = team;
      // Clear trailer selection when user changes team (not during patching)
      if (!this.patchingTrailers) {
        this.vm.fuelTankSelectedTrailer = undefined;
        this.vm.fuelTankSelectedTrailerName = '';
      }
      this.vm.trailers = [];
      this.loadTrailersForFuelTank(team);
    } else {
      this.vm.fuelTankSelectedTeam = undefined;
      this.vm.fuelTankSelectedTrailer = undefined;
      this.vm.fuelTankSelectedTrailerName = '';
      this.vm.trailers = [];
    }
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles trailer division selection change.
   *
   * @param {number} divisionId The selected division ID
   */
  public onTrailerDivisionChange(divisionId: number): void {
    if (isDefined(divisionId)) {
      this.vm.trailerSelectedDivision = divisionId;

      // Load regions for this division
      this.loadRegions(divisionId);
    } else {
      this.vm.trailerSelectedDivision = undefined;
    }
    // Clear dependent fields
    this.vm.regions = [];
    this.vm.trailerSelectedRegion = undefined;
    this.vm.teams = [];
    this.vm.selectedTeam = undefined;
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Handles trailer region selection change.
   *
   * @param {number} regionId The selected region ID
   */
  public onTrailerRegionChange(regionId: number): void {
    if (isDefined(regionId)) {
      this.vm.trailerSelectedRegion = regionId;

      // Load teams for this region
      this.loadTeams(regionId);
    } else {
      this.vm.trailerSelectedRegion = undefined;
    }
    // Clear dependent fields
    this.vm.teams = [];
    this.vm.selectedTeam = undefined;
    this.cdr.detectChanges();
    this.onChangeSave();
  }

  /**
   * Updates an existing generator with the provided form data.
   * Validates required fields and submits the generator payload to the service.
   */
  public updateGenerator(): void {
    if (!this.isGeneratorFormValid()) {
      this.openMessageDialog('Please fill all required fields.');
      return;
    }

    const generatorAssetId: string | undefined = this.vm.generatorId ?? this.vm.dbId;
    const timeValue: string = new Date().toISOString();

    // Resolve names from IDs at save time (matching desktop pattern)
    const divisionName: string = this.vm.divisions.find(
      (divisionData: JsonHierarchyElement): boolean => divisionData.elementID === this.vm.selectedDivision
    )?.name?.toString() ?? '';
    const regionName: string = this.vm.regions.find(
      (regionData: JsonHierarchyElement): boolean => regionData.elementID === this.vm.selectedRegion
    )?.name?.toString() ?? '';
    const statusObj: StatusTypeDTO | undefined = this.vm.statuses.find(
      (statusData: StatusTypeDTO): boolean => statusData.id === this.vm.selectedStatus
    );
    const statusName: string = statusObj?.statusType ?? '';
    const manufacturerObj: ManufactureTypeDTO | undefined = this.vm.manufacturers.find(
      (manufacturerData: ManufactureTypeDTO): boolean => manufacturerData.id === this.vm.selectedManufacturer
    );
    const manufacturerName: string = manufacturerObj?.manufacture ?? '';
    const modelObj: ModelTypeDTO | undefined = this.vm.models.find(
      (modelData: ModelTypeDTO): boolean => modelData.id === this.vm.selectedModel
    );
    const modelName: string = modelObj?.genModel ?? '';

    const generatorPayload: Record<string, unknown> = {
      address: this.vm.address,
      city: this.vm.city,
      comments: this.vm.comments,
      currentType: this.vm.selectedCurrentType,
      division: divisionName,
      generatorChain: this.vm.generatorChain,
      generatorCord: this.vm.generatorCord,
      generatorId: generatorAssetId,
      generatorLastModified: timeValue,
      generatorLastModifiedBy: this.userName,
      generatorLock: this.vm.generatorLock,
      generatorManufactureId: this.vm.selectedManufacturer,
      generatorManufacturer: manufacturerName,
      generatorModel: modelName,
      generatorModelId: this.vm.selectedModel,
      generatorSerialNumber: this.vm.serialNumber,
      generatorStatus: statusName,
      generatorStatusId: this.vm.selectedStatus,
      latitude: this.vm.generatorLocationInfoForm?.value.currentGpsLatitude,
      loanedTeamName: this.vm.selectedLoanedToTeam,
      localId: this.vm.localId,
      longitude: this.vm.generatorLocationInfoForm?.value.currentGpsLongitude,
      maintenance: this.vm.selectedMaintenance,
      outputVoltage: this.vm.selectedVoltage,
      ownerContact: this.vm.ownerContact,
      qrCodeId: this.vm.assetId,
      region: regionName,
      state: this.vm.selectedState,
      teamName: this.vm.selectedTeam,
      trailerId: this.vm.selectedTrailer,
      zipcode: this.vm.zipcode
    };

    this.startLoading();
    this.generatorService.updateGenerator(Number(generatorAssetId), generatorPayload)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.stopLoading();
        })
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error updating generator. Please try again.');
        },
        next: (response: BaseResponseDTO): void => {
          if (response?.success) {
            let successMessage: string = response?.message ?? 'Generator updated successfully.';
            if (isDefinedNotNullNotEmpty(successMessage)) {
              successMessage = successMessage.charAt(0).toUpperCase() + successMessage.slice(1);
            }
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroyed$)).subscribe((): void => {
              if (response?.success) {
                this.location.back();
              }
            });
          } else {
            let errorMessage: string = response?.message ?? 'Error updating generator. Please try again.';
            if (isDefinedNotNullNotEmpty(errorMessage)) {
              errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
            }
            this.openMessageDialog(errorMessage);
          }
        }
      });
  }

  /**
   * Updates an existing trailer with the provided form data.
   * Validates required fields and submits the trailer payload to the service.
   */
  public updateTrailer(): void {
    if (!this.isTrailerFormValid()) {
      this.openMessageDialog('Please fill all required fields for Trailer.');
      return;
    }

    const trailerId: string | undefined = this.vm.trailerId ?? this.vm.dbId;
    const timeValue: string = new Date().toISOString();

    // Resolve names from IDs at save time (matching desktop pattern)
    const divisionName: string = this.vm.divisions.find(
      (divisionData: JsonHierarchyElement): boolean => divisionData.elementID === this.vm.trailerSelectedDivision
    )?.name?.toString() ?? '';
    const regionName: string = this.vm.regions.find(
      (regionData: JsonHierarchyElement): boolean => regionData.elementID === this.vm.trailerSelectedRegion
    )?.name?.toString() ?? '';
    const statusObj: StatusTypeDTO | undefined = this.vm.statuses.find(
      (s: StatusTypeDTO): boolean => s.id === this.vm.trailerSelectedStatus
    );
    const statusName: string = statusObj?.statusType ?? '';

    const trailerPayload: Record<string, unknown> = {
      awayFromHome: this.vm.trailerAwayFromHome,
      cdlRequired: this.vm.trailerCdlRequired === 1 ? 1 : 0,
      city: this.vm.trailerCity,
      comments: this.vm.trailerComments,
      currentLatitude: this.vm.trailerLocationInfoForm?.value.currentGpsLatitude,
      currentLongitude: this.vm.trailerLocationInfoForm?.value.currentGpsLongitude,
      division: divisionName,
      generatorCapacity: this.vm.trailerGeneratorCapacity ? Number(this.vm.trailerGeneratorCapacity) : undefined,
      hitchSize: this.vm.trailerHitchSize,
      hitchType: this.vm.trailerHitchType,
      houseNumber: this.vm.trailerHouseNumber,
      licensePlate: this.vm.trailerLicensePlate,
      localId: this.vm.trailerLocalId,
      lockCombination: this.vm.trailerLockCombination,
      lockType: this.vm.trailerLockType,
      primaryContact: this.vm.trailerPrimaryContact,
      qrCodeId: this.vm.trailerAssetId,
      region: regionName,
      secondaryContact: this.vm.trailerSecondaryContact,
      serialNumber: this.vm.trailerSerialNumber,
      state: this.vm.trailerSelectedState,
      status: statusName,
      street: this.vm.trailerStreet,
      teamName: this.vm.selectedTeam,
      trailerHeight: this.vm.trailerHeight,
      trailerLastModifiedByUserId: this.userName,
      trailerLastModifiedDate: timeValue,
      trailerLoanedTeamId: this.vm.trailerSelectedLoanedToTeam,
      trailerName: this.vm.trailerName,
      trailerSize: this.vm.trailerSize,
      zipCode: this.vm.trailerZip
    };

    this.startLoading();
    this.generatorService.updateTrailer(Number(trailerId), trailerPayload)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.stopLoading();
        })
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error updating trailer. Please try again.');
        },
        next: (response: BaseResponseDTO): void => {
          if (response?.success) {
            let successMessage: string = response?.message ?? 'Trailer updated successfully.';
            if (isDefinedNotNullNotEmpty(successMessage)) {
              successMessage = successMessage.charAt(0).toUpperCase() + successMessage.slice(1);
            }
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroyed$)).subscribe((): void => {
              if (response?.success) {
                this.location.back();
              }
            });
          } else {
            let errorMessage: string = response?.message ?? 'Error updating trailer. Please try again.';
            if (isDefinedNotNullNotEmpty(errorMessage)) {
              errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
            }
            this.openMessageDialog(errorMessage);
          }
        }
      });
  }

  /**
   * Updates an existing fuel tank with the provided form data.
   * Validates required fields and submits the fuel tank payload to the service.
   */
  public updateFuelTank(): void {
    if (!this.isFuelTankFormValid()) {
      this.openMessageDialog('Please fill all required fields for Fuel Tank.');
      return;
    }

    const fuelTankId: string | undefined = this.vm.fuelTankId ?? this.vm.dbId;
    const timeValue: string = new Date().toISOString();

    // Resolve names from IDs at save time (matching desktop pattern)
    const divisionName: string = this.vm.divisions.find(
      (divisionData: JsonHierarchyElement): boolean => divisionData.elementID === this.vm.fuelTankSelectedDivision
    )?.name?.toString() ?? '';
    const regionName: string = this.vm.regions.find(
      (regionData: JsonHierarchyElement): boolean => regionData.elementID === this.vm.fuelTankSelectedRegion
    )?.name?.toString() ?? '';
    const statusObj: StatusTypeDTO | undefined = this.vm.statuses.find(
      (statusData: StatusTypeDTO): boolean => statusData.id === this.vm.fuelTankSelectedStatus
    );
    const statusName: string = statusObj?.statusType ?? '';
    const selectedTrailer: TrailerNameIdDTO | undefined = this.vm.trailers.find(
      (trailerData: TrailerNameIdDTO): boolean => trailerData.trailerId === this.vm.fuelTankSelectedTrailer
    );
    const trailerName: string = selectedTrailer?.trailerName ?? '';

    const fuelTankPayload: Record<string, unknown> = {
      address: this.vm.fuelTankAddress,
      capacity: this.vm.fuelTankSelectedCapacity,
      city: this.vm.fuelTankCity,
      comments: this.vm.fuelTankComments,
      division: divisionName,
      lastModifiedByUserId: this.userName,
      lastModifiedDate: timeValue,
      latitude: this.vm.fuelTankLocationInfoForm?.value.currentGpsLatitude,
      loanedToTeam: this.vm.fuelTankLoanedToTeam,
      localId: this.vm.fuelTankLocalId,
      longitude: this.vm.fuelTankLocationInfoForm?.value.currentGpsLongitude,
      qrCodeId: this.vm.fuelTankAssetId,
      region: regionName,
      serialNumber: this.vm.fuelTankSerialNumber,
      state: this.vm.fuelTankState,
      status: statusName,
      teamName: this.vm.fuelTankSelectedTeam,
      trailerId: this.vm.fuelTankSelectedTrailer,
      trailerName,
      zipCode: this.vm.fuelTankZipcode
    };

    this.startLoading();
    this.generatorService.updateFuelTank(Number(fuelTankId), fuelTankPayload)
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.stopLoading();
        })
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error updating fuel tank. Please try again.');
        },
        next: (response: BaseResponseDTO): void => {
          if (response?.success) {
            let successMessage: string = response?.message ?? 'Fuel Tank updated successfully.';
            if (isDefinedNotNullNotEmpty(successMessage)) {
              successMessage = successMessage.charAt(0).toUpperCase() + successMessage.slice(1);
            }
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroyed$)).subscribe((): void => {
              if (response?.success) {
                this.location.back();
              }
            });
          } else {
            let errorMessage: string = response?.message ?? 'Error updating fuel tank. Please try again.';
            if (isDefinedNotNullNotEmpty(errorMessage)) {
              errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
            }
            this.openMessageDialog(errorMessage);
          }
        }
      });
  }

  /**
   * Validates the generator form to ensure all required fields are filled.
   *
   * @returns {boolean} True if the form is valid, false otherwise
   */
  private isGeneratorFormValid(): boolean {
    // Check voltage properly - allow 0 as a valid value, but not empty string/undefined
    const hasVoltage: boolean = isDefinedNotNullNotEmpty(this.vm.selectedVoltage);

    return Boolean(
      this.vm.selectedStatus &&
      this.vm.serialNumber &&
      this.vm.selectedManufacturer &&
      this.vm.selectedModel &&
      this.vm.selectedCurrentType &&
      hasVoltage &&
      this.vm.selectedDivision &&
      this.vm.selectedRegion &&
      this.vm.selectedTeam
    );
  }

  /**
   * Validates the trailer form to ensure all required fields are filled.
   *
   * @returns {boolean} True if the form is valid, false otherwise
   */
  private isTrailerFormValid(): boolean {
    const cdlSelected: boolean = (this.vm.trailerCdlRequired === 1 || this.vm.trailerCdlRequired === 0);
    return Boolean(
      this.vm.trailerSelectedStatus &&
      this.vm.trailerSize &&
      this.vm.trailerHeight &&
      this.vm.selectedTeam &&
      this.vm.trailerSelectedDivision &&
      this.vm.trailerSelectedRegion &&
      this.vm.trailerHouseNumber &&
      this.vm.trailerStreet &&
      this.vm.trailerCity &&
      this.vm.trailerSelectedState &&
      this.vm.trailerZip &&
      this.vm.trailerLicensePlate &&
      cdlSelected &&
      this.vm.trailerHitchSize &&
      this.vm.trailerHitchType &&
      this.vm.trailerPrimaryContact &&
      this.vm.trailerLockType &&
      this.vm.trailerLockCombination
    );
  }

  /**
   * Validates the fuel tank form to ensure all required fields are filled.
   *
   * @returns {boolean} True if the form is valid, false otherwise
   */
  private isFuelTankFormValid(): boolean {
    return Boolean(
      this.vm.fuelTankSelectedCapacity &&
      this.vm.fuelTankSelectedDivision &&
      this.vm.fuelTankSelectedRegion &&
      this.vm.fuelTankSelectedStatus &&
      this.vm.fuelTankSelectedTeam
    );
  }

  /**
   * Updates the save button state based on form validation status.
   * Checks validity of the active form (generator, trailer, or fuel tank) and enables/disables the button accordingly.
   */
  public onChangeSave(): void {
    let isFormValid: boolean = false;

    const assetType: string | undefined = this.vm.assetType;
    switch (assetType) {
      case 'Generator': {
        const locationValid: boolean =
          isUndefined(this.vm.generatorLocationInfoForm) || this.vm.generatorLocationInfoForm.valid;
        isFormValid = this.isGeneratorFormValid() && locationValid;
        break;
      }
      case 'Trailer': {
        const locationValid: boolean =
          isUndefined(this.vm.trailerLocationInfoForm) || this.vm.trailerLocationInfoForm.valid;
        isFormValid = this.isTrailerFormValid() && locationValid;
        break;
      }
      case 'Fueltank': {
        const locationValid: boolean =
          isUndefined(this.vm.fuelTankLocationInfoForm) || this.vm.fuelTankLocationInfoForm.valid;
        isFormValid = this.isFuelTankFormValid() && locationValid;
        break;
      }
      default:
        isFormValid = false;
        break;
    }

    // Enable/disable the save button
    // Pass boolean: true = disable button, false = enable button
    if (isFormValid) {
      this.data.generatorEditSaveEnabledMessage('generatorEditForm', false);
    } else {
      this.data.generatorEditSaveEnabledMessage('generatorEditForm', true);
    }
  }

  /**
   * Increments the active request counter and shows the progress bar.
   */
  private startLoading(): void {
    this.activeRequests++;
    this.vm.showProgressSpinner = true;
    this.cdr.detectChanges();
  }

  /**
   * Decrements the active request counter and hides the progress bar when all requests complete.
   */
  private stopLoading(): void {
    this.activeRequests--;
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.vm.showProgressSpinner = false;
    }
    this.cdr.detectChanges();
  }

  /**
   * Opens a message dialog safely, ensuring only one dialog is open at a time.
   *
   * @param {string} message The message to display in the dialog
   * @returns {MatDialogRef<PowerSupplyMessageDialog>} The dialog reference
   */
  private openMessageDialog(message: string): MatDialogRef<PowerSupplyMessageDialog> {
    // Close existing dialog if one is already open
    if (isDefined(this.activeDialog) && this.activeDialog.getState() === MatDialogState.OPEN) {
      this.activeDialog.close();
    }

    // Open new dialog
    this.activeDialog = this.dialog.open(PowerSupplyMessageDialog, {
      data: { message }
    });

    // Clear reference when dialog is closed
    this.activeDialog.afterClosed().pipe(take(1), takeUntil(this.destroyed$)).subscribe((): void => {
      this.activeDialog = undefined;
    });

    return this.activeDialog;
  }

  /**
   * Handles location form value changes and updates GPS coordinates in the view model.
   *
   * @param {FormGroup | undefined} form The location form group containing GPS latitude and longitude values
   */
  public onChangeLocation(form: FormGroup | undefined): void {
    if (isDefined(form)) {
      this.vm.currentGpsLatitude = form.value.currentGpsLatitude;
      this.vm.currentGpsLongitude = form.value.currentGpsLongitude;
      this.cdr.detectChanges();
    }
    // Trigger button state update when location changes
    this.onChangeSave();
  }

  /**
   * Trims leading and trailing whitespace from a field in the view model.
   *
   * @param {string} fieldName The name of the field in the view model to trim
   */
  public trimGeneratorLeadingAndTrailingSpaces(fieldName: string): void {
    this.trimField(fieldName);
  }

  /**
   * Trims a field value if it exists in the view model and is a string.
   *
   * @param {string} fieldName The name of the field to trim
   */
  private trimField(fieldName: string): void {
    if (isDefined(this.vm[fieldName]) && typeof this.vm[fieldName] === 'string') {
      this.vm[fieldName] = this.vm[fieldName].trim();
      this.cdr.detectChanges();
    }
  }

  /**
   * Trims leading and trailing whitespace from a field in the view model.
   *
   * @param {string} fieldName The name of the field to trim
   */
  public trimSpaces(fieldName: string): void {
    this.trimField(fieldName);
  }

  /**
   * Validates that only numeric characters are entered in an input field.
   *
   * @param {KeyboardEvent} event The keyboard event to validate
   * @returns {boolean} True if the character is numeric, false otherwise
   */
  public numberOnly(event: KeyboardEvent): boolean {
    const charCode: number | undefined = (event.which) ? event.which : event.keyCode;
    if (charCode > this.CONTROL_CHAR_MAX && (charCode < this.CHAR_CODE_ZERO || charCode > this.CHAR_CODE_NINE)) {
      return false;
    }
    return true;
  }

  /**
   * Prevents pasting of non-numeric text into input fields.
   *
   * @param {ClipboardEvent} event The clipboard event containing the pasted data
   */
  public restrictPasteForText(event: ClipboardEvent): void {
    const clipboardData: DataTransfer | undefined = event.clipboardData ?? undefined;
    const pastedText: string | undefined = clipboardData?.getData('text');
    if (isDefined(pastedText) && !/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  // TrackBy functions for performance optimization
  /**
   * TrackBy function for status list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {StatusTypeDTO} item The status item
   * @returns {number} The unique identifier for the status item
   */
  public trackByStatus(index: number, item: StatusTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for manufacturer list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {ManufactureTypeDTO} item The manufacturer item
   * @returns {number} The unique identifier for the manufacturer item
   */
  public trackByManufacturer(index: number, item: ManufactureTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for model list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {ModelTypeDTO} item The model item
   * @returns {number} The unique identifier for the model item
   */
  public trackByModel(index: number, item: ModelTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for current type list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {CurrentTypeDTO} item The current type item
   * @returns {number | string} The unique identifier for the current type item
   */
  public trackByCurrentType(index: number, item: CurrentTypeDTO): number | string {
    return item.currentType ?? index;
  }

  /**
   * TrackBy function for voltage type list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {VoltageTypeDTO} item The voltage type item
   * @returns {number} The unique identifier for the voltage type item
   */
  public trackByVoltageType(index: number, item: VoltageTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for division list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {JsonHierarchyElement} item The division item
   * @returns {number} The unique identifier for the division item
   */
  public trackByDivision(index: number, item: JsonHierarchyElement): number {
    return item.elementID ?? index;
  }

  /**
   * TrackBy function for region list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {JsonHierarchyElement} item The region item
   * @returns {number} The unique identifier for the region item
   */
  public trackByRegion(index: number, item: JsonHierarchyElement): number {
    return item.elementID ?? index;
  }

  /**
   * TrackBy function for team list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @returns {number} The index as the unique identifier
   */
  public trackByTeam(index: number): number {
    return index;
  }

  /**
   * TrackBy function for trailer list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {TrailerNameIdDTO} item The trailer item
   * @returns {number} The unique identifier for the trailer item
   */
  public trackByTrailerId(index: number, item: TrailerNameIdDTO): number {
    return item.trailerId ?? index;
  }

  /**
   * TrackBy function for state list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {StatesEntity} item The state item
   * @returns {number | string} The unique identifier for the state item
   */
  public trackByState(index: number, item: StatesEntity): number | string {
    return item.name ?? index;
  }

  /**
   * TrackBy function for maintenance list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {MaintenanceTypeDTO} item The maintenance item
   * @returns {number} The unique identifier for the maintenance item
   */
  public trackByMaintenance(index: number, item: MaintenanceTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for fuel tank size list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {FuelTankSizeDTO} item The fuel tank size item
   * @returns {number} The unique identifier for the fuel tank size item
   */
  public trackByFuelTankSize(index: number, item: FuelTankSizeDTO): number {
    return item.id ?? index;
  }

  // Generator Scanner Methods
  /**
   * Toggles the QR code scanner for generator asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableScanner(): void {
    if (this.vm.scannerEnabled) {
      this.vm.zoomFlag = false;
      this.vm.scannerEnabled = false;
    } else {
      this.vm.scannedValue = '';
      this.vm.zoomLevel = 1;
      this.vm.zoomFlag = true;
      this.vm.scannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for generator asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public scanSuccessHandler(event: string): void {
    this.vm.scannedValue = event;
    this.vm.scannerEnabled = false;
    this.vm.zoomFlag = false;
    this.vm.assetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the generator scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public zoomIn(): void {
    if (this.vm.zoomLevel < 1.4) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel + 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyZoom();
    }
  }

  /**
   * Decreases the zoom level of the generator scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public zoomOut(): void {
    if (this.vm.zoomLevel > 1) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel - 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyZoom();
    }
  }

  /**
   * Applies the current zoom level to the scanner element.
   */
  private applyZoom(): void {
    if (isDefined(this.scannerElement?.nativeElement)) {
      this.scannerElement.nativeElement.style.transform = `scale(${this.vm.zoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.zoomLevel})`;
    }
  }

  // Trailer Scanner Methods
  /**
   * Toggles the QR code scanner for trailer asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableTrailerScanner(): void {
    if (this.vm.trailerScannerEnabled) {
      this.vm.trailerZoomFlag = false;
      this.vm.trailerScannerEnabled = false;
    } else {
      this.vm.trailerScannedValue = '';
      this.vm.trailerZoomLevel = 1;
      this.vm.trailerZoomFlag = true;
      this.vm.trailerScannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for trailer asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public trailerScanSuccessHandler(event: string): void {
    this.vm.trailerScannedValue = event;
    this.vm.trailerScannerEnabled = false;
    this.vm.trailerZoomFlag = false;
    this.vm.trailerAssetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the trailer scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public trailerZoomIn(): void {
    if (this.vm.trailerZoomLevel < 1.4) {
      this.vm.trailerZoomLevel = Math.round((this.vm.trailerZoomLevel + 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyTrailerZoom();
    }
  }

  /**
   * Decreases the zoom level of the trailer scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public trailerZoomOut(): void {
    if (this.vm.trailerZoomLevel > 1) {
      this.vm.trailerZoomLevel = Math.round((this.vm.trailerZoomLevel - 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyTrailerZoom();
    }
  }

  /**
   * Applies the current zoom level to the trailer scanner element.
   */
  private applyTrailerZoom(): void {
    if (isDefined(this.trailerScannerElement?.nativeElement)) {
      this.trailerScannerElement.nativeElement.style.transform = `scale(${this.vm.trailerZoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.trailerZoomLevel})`;
    }
  }

  // Fuel Tank Scanner Methods
  /**
   * Toggles the QR code scanner for fuel tank asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableFuelTankScanner(): void {
    if (this.vm.fuelTankScannerEnabled) {
      this.vm.fuelTankZoomFlag = false;
      this.vm.fuelTankScannerEnabled = false;
    } else {
      this.vm.fuelTankScannedValue = '';
      this.vm.fuelTankZoomLevel = 1;
      this.vm.fuelTankZoomFlag = true;
      this.vm.fuelTankScannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for fuel tank asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public fuelTankScanSuccessHandler(event: string): void {
    this.vm.fuelTankScannedValue = event;
    this.vm.fuelTankScannerEnabled = false;
    this.vm.fuelTankZoomFlag = false;
    this.vm.fuelTankAssetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the fuel tank scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public fuelTankZoomIn(): void {
    if (this.vm.fuelTankZoomLevel < 1.4) {
      this.vm.fuelTankZoomLevel = Math.round((this.vm.fuelTankZoomLevel + 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyFuelTankZoom();
    }
  }

  /**
   * Decreases the zoom level of the fuel tank scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public fuelTankZoomOut(): void {
    if (this.vm.fuelTankZoomLevel > 1) {
      this.vm.fuelTankZoomLevel = Math.round((this.vm.fuelTankZoomLevel - 0.1) * 10) / 10;
      this.cdr.detectChanges();
      this.applyFuelTankZoom();
    }
  }

  /**
   * Applies the current zoom level to the fuel tank scanner element.
   */
  private applyFuelTankZoom(): void {
    if (isDefined(this.fuelTankScannerElement?.nativeElement)) {
      this.fuelTankScannerElement.nativeElement.style.transform = `scale(${this.vm.fuelTankZoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.fuelTankZoomLevel})`;
    }
  }
}
